import React, { useRef, useState, useEffect } from "react";
import _ from "lodash";
import { Button, Tooltip, Upload, Typography, Space } from "antd";
import { ExportOutlined, SaveOutlined, UploadOutlined } from "@ant-design/icons";
import { JsonEditor } from "./JsonEditor";
import { FIELD_DATA_STRUCTURE } from "./sharedData";
import { updateJson } from "./updateJson";
import { beUIFieldNamesMap, feBEfieldNamesMap, reversedBeUIFieldNamesMap } from "./constants";
import { PopupStyledModal } from "../customComponentNewDesign/CustomPopupModal";
import { GRAY_500 } from "../../constant/colors";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

const defaultColSize = 12;
const datepickerDefaultColSize = 8;

const expectedHeaders = Object.values(FIELD_DATA_STRUCTURE);

const buildFieldName = (values) => {
  const sectionIndex = expectedHeaders.findIndex((header) => header === FIELD_DATA_STRUCTURE.SECTION_NAME);
  const sectionName = values[sectionIndex];

  const nameIndex = expectedHeaders.findIndex((header) => header === FIELD_DATA_STRUCTURE.FE_NAME);
  const fieldName = values[nameIndex];

  const subSectionIndex = expectedHeaders.findIndex((header) => header === FIELD_DATA_STRUCTURE.SUB_SECTION_NAME);
  const subSectionName = values[subSectionIndex];

  return subSectionName ? `${sectionName}.${subSectionName}.${fieldName}` : `${sectionName}.${fieldName}`;
};

export const TsvToJson = ({ setJson, json }) => {
  const { t } = useTranslation();

  const jsonEditorRef = useRef(null);
  const lastSavedJsonRef = useRef(json);
  const [shouldUpdateUI, setShouldUpdateUI] = useState(false);
  const [shouldUpdateJson, setShouldUpdateJson] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  useEffect(() => {
    lastSavedJsonRef.current = json;
    setShouldUpdateUI(false);
  }, [json]);

  const parseJson = (text) => {
    const jsonData = JSON.parse(text);
    if (!jsonData.formSections) return jsonData;

    const convertedJson = { ...jsonData };
    convertedJson.formSections = convertedJson.formSections.map((section) => ({
      ...section,
      rows: section.rows.map((row) => ({
        ...row,
        fields: row.fields.map((field) => ({
          ...field,
          BEName: reversedBeUIFieldNamesMap[field.BEName] || field.BEName
        }))
      }))
    }));

    return convertedJson;
  };

  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const fileName = file.name;
      const fileExtension = fileName.split(".").pop().toLowerCase(); // Extract the extension
      const isTsv = fileExtension === "tsv";
      const structuredJson = isTsv ? parseTSV(text) : parseJson(text);
      updateJson(structuredJson.formSections, setJson);
      setShouldUpdateUI(false);
      setShouldUpdateJson(false);
    };
    reader.readAsText(file);
    return false;
  };

  const buildField = (row) => ({
    type: row[FIELD_DATA_STRUCTURE.TYPE],
    name: row[FIELD_DATA_STRUCTURE.FE_NAME],
    BEName: row[FIELD_DATA_STRUCTURE.BE_NAME],
    FHIRName: row[FIELD_DATA_STRUCTURE.FHIR_NAME],
    subSectionName: row[FIELD_DATA_STRUCTURE.SUB_SECTION_NAME],
    label: row[FIELD_DATA_STRUCTURE.LABEL],
    rules: [],
    required: row[FIELD_DATA_STRUCTURE.REQUIRED] || false,
    placeholder: row[FIELD_DATA_STRUCTURE.PLACEHOLDER],
    colSize: row[FIELD_DATA_STRUCTURE.TYPE] !== "datepicker" ? defaultColSize : datepickerDefaultColSize,
    defaultValue: row[FIELD_DATA_STRUCTURE.DEFAULT_VALUE],
    alternativeFields: row[FIELD_DATA_STRUCTURE.ALTERNATIVE_FIELDS]?.split(",").map((item) => item.trim()),
    fieldProps: {},
    formItemProps: {},
    dedicatedFieldProps: {
      ...(row[FIELD_DATA_STRUCTURE.TYPE] === "phone" && { typeName: "phoneType", addon: true }),
      ...(row[FIELD_DATA_STRUCTURE.TYPE] === "gender" && { showThirdOption: true, thirdOption: null }),
      ...(row[FIELD_DATA_STRUCTURE.TYPE] === "radio" && {
        direction: "horizontal",
        optionItems: row[FIELD_DATA_STRUCTURE.OPTION_ITEMS]?.map((option) => ({
          value: option,
          displayValue: option
        })) || [
          {
            value: "example",
            displayValue: "Example"
          }
        ]
      }),
      ...(row[FIELD_DATA_STRUCTURE.TYPE] === "checkboxGroup" && {
        direction: "horizontal",
        optionItems: row[FIELD_DATA_STRUCTURE.OPTION_ITEMS] || [
          {
            value: "example",
            displayValue: "Example"
          }
        ]
      }),
      ...(row[FIELD_DATA_STRUCTURE.TYPE] === "singleSelect" && {
        useCommonStyle: false,
        bordered: true,
        optionItems: row[FIELD_DATA_STRUCTURE.OPTION_ITEMS]?.map((option) => ({
          value: option,
          displayValue: option
        })) || [
          {
            value: "example",
            displayValue: "Example"
          }
        ]
      })
    }
  });

  const parseSections = (rows) => {
    let rowIndex = 0;

    const sectionsObj = rows.reduce((acc, row) => {
      const sectionName = row[FIELD_DATA_STRUCTURE.SECTION_NAME];
      if (!acc[sectionName]) {
        acc[sectionName] = {
          sectionName: sectionName,
          sectionHeader: row[FIELD_DATA_STRUCTURE.SECTION_HEADER],
          sectionSidebar: row[FIELD_DATA_STRUCTURE.SECTION_SIDEBAR],
          rows: [
            {
              rowIndex: rowIndex++,
              fields: []
            }
          ]
        };
      }
      acc[sectionName].rows[0].fields.push(buildField(row));
      return acc;
    }, {});

    const sectionsArr = Object.values(sectionsObj);

    return sectionsArr;
  };

  const buildStructuredJson = (rows) => {
    const structuredJson = { formSections: [] };
    const formSections = parseSections(rows);
    structuredJson.formSections = formSections;

    return structuredJson;
  };

  const parseTSV = (text) => {
    const lines = text.split("\n");
    const headers = lines[0].split("\t").map((header) => header.trim());

    if (!_.isEqual(headers, expectedHeaders)) {
      alert("File headers do not match the expected format.");
      return null;
    }

    const rows = lines.slice(1).map((line) => {
      const values = line.split("\t").map((value) => value.trim());
      const processedRow = {};

      expectedHeaders.forEach((header, index) => {
        let value = values[index];
        if (header === FIELD_DATA_STRUCTURE.SECTION_NAME && !value) {
          return;
        }
        if (!value) {
          value = header === FIELD_DATA_STRUCTURE.REQUIRED ? false : null;
        } else {
          switch (header) {
            case FIELD_DATA_STRUCTURE.OPTION_ITEMS:
              // For "optionItems" header, split the string by ";" and trim each part to create an array
              value = value.split(";").map((item) => item.trim());
              break;
            case FIELD_DATA_STRUCTURE.REQUIRED:
              value = value.toLowerCase() === "true" ? true : false;
              break;
          }
        }
        processedRow[header] = value;
      });

      processedRow["colSize"] =
        processedRow[FIELD_DATA_STRUCTURE.TYPE] !== "datepicker" ? defaultColSize : datepickerDefaultColSize;

      const fieldName = buildFieldName(values);
      const beName = feBEfieldNamesMap[fieldName];
      processedRow[FIELD_DATA_STRUCTURE.BE_NAME] = reversedBeUIFieldNamesMap[beName] || null;
      return processedRow;
    });

    const structuredJson = buildStructuredJson(rows);
    return structuredJson;
  };

  const handleSvgExport = () => {
    // TODO: Implement this
  };

  const handleJSONExport = () => {
    if (shouldUpdateUI) {
      setIsModalVisible(true);
      return;
    }
    exportJSONData();
  };

  const exportJSONData = () => {
    const jsonToExport = { ...json };
    jsonToExport.formSections.forEach((section) => {
      section.rows.forEach((row) => {
        row.fields.forEach((field) => {
          field[FIELD_DATA_STRUCTURE.BE_NAME] = beUIFieldNamesMap[field[FIELD_DATA_STRUCTURE.BE_NAME]] || null;
        });
      });
    });

    const jsonStr = JSON.stringify(jsonToExport, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "data.json";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    closeModal();
  };

  const handleSave = () => {
    if (jsonEditorRef.current) {
      jsonEditorRef.current.handleSave();
    }
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      <PopupStyledModal
        open={isModalVisible}
        destroyOnClose={true}
        title={t("online_wizard.unsaved_changes")}
        centered
        onCancel={closeModal}
        width="620px"
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={closeModal} id="online_wizard_cancel_export_json">
              {t("cancel")}
            </Button>
            <Button htmlType="submit" type="primary" onClick={exportJSONData} id="online_wizard_export_json">
              {t("online_wizard.export")}
            </Button>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Space style={{ display: "flex", justifyContent: "flex-end", paddingTop: 16 }}>
            <Text style={{ color: GRAY_500 }}>{t("online_wizard.json_export_pending_changes")}</Text>
          </Space>
        </div>
      </PopupStyledModal>

      {/* TODO: add option to export SVG file */}
      <Tooltip placement="top" title={"Will implements soon"}>
        <Button type="primary" onClick={handleSvgExport} icon={<ExportOutlined />} style={{ margin: 5 }} disabled>
          SVG
        </Button>
      </Tooltip>
      <Button type="primary" onClick={handleJSONExport} icon={<ExportOutlined />} style={{ margin: 5 }}>
        JSON {shouldUpdateJson ? "*" : ""}
      </Button>
      <Button type="primary" onClick={handleSave} icon={<SaveOutlined />} style={{ margin: 5 }}>
        Update UI {shouldUpdateUI ? "*" : ""}
      </Button>
      <Upload accept=".tsv,.json" beforeUpload={handleFileUpload} showUploadList={false}>
        <Button icon={<UploadOutlined />} style={{ margin: 5 }}>
          Upload file
        </Button>
      </Upload>
      <JsonEditor
        ref={jsonEditorRef}
        json={json}
        onChange={setJson}
        setShouldUpdateUI={setShouldUpdateUI}
        lastSavedJsonRef={lastSavedJsonRef}
      />
    </>
  );
};
